import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api/client";
import {
  INSTRUCTION_WORD_LIMIT,
  countWords,
  formatInstructionWordCount,
  type InstructionSummarizeKind,
} from "@/lib/instruction-limit";
import {
  cancelSummarizeJob,
  deleteServerSummarize,
  listSummarizeJobs,
  startSummarizeJob,
  subscribeSummarizeJobs,
  type SummarizeJob,
} from "@/lib/summarize-jobs";
import { cn } from "@/lib/utils";

interface InstructionWordLimitProps {
  text: string;
  kind: InstructionSummarizeKind;
  onSummarized: (summary: string) => void;
  disabled?: boolean;
  /** Shown with the Summarize button when the text is over the limit. */
  note?: string;
  /** Reference PDF row: Ready when the extract is within the limit. */
  showStatus?: boolean;
  /**
   * When set, summarization keeps running after this control unmounts.
   * Removing the PDF or Cancel aborts the request.
   */
  track?: {
    key: string;
    scope: SummarizeJob["scope"];
    ownerId: string;
    fileName: string;
    savedDocumentId?: string;
  };
}

export function instructionsExceedLimit(text: string): boolean {
  return countWords(text) > INSTRUCTION_WORD_LIMIT;
}

export function UploadStatusText({ status }: { status: "ready" | "not-ready" | null }) {
  if (!status) return null;
  const ready = status === "ready";
  return (
    <span
      className={cn(
        "shrink-0 text-xs font-medium",
        ready
          ? "text-emerald-700 dark:text-emerald-400"
          : "text-[var(--tint-red-fg)] dark:text-red-400"
      )}
    >
      {ready ? "Ready" : "Not ready"}
    </span>
  );
}

export function InstructionWordLimit({
  text,
  kind,
  onSummarized,
  disabled = false,
  note,
  showStatus = false,
  track,
}: InstructionWordLimitProps) {
  const [localSummarizing, setLocalSummarizing] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [jobTick, setJobTick] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const serverJobRef = useRef<string | null>(null);
  const onSummarizedRef = useRef(onSummarized);
  onSummarizedRef.current = onSummarized;
  const words = countWords(text);
  const overLimit = words > INSTRUCTION_WORD_LIMIT;
  const trackedJob = track
    ? listSummarizeJobs(track.scope, track.ownerId).find((job) => job.key === track.key)
    : undefined;
  void jobTick;
  const isSummarizing = track ? trackedJob?.status === "summarizing" : localSummarizing;
  const error = track
    ? trackedJob?.status === "failed"
      ? trackedJob.error ?? "Could not summarize instructions."
      : null
    : localError;
  const status = showStatus ? (isSummarizing || overLimit ? "not-ready" : "ready") : null;

  useEffect(() => subscribeSummarizeJobs(() => setJobTick((value) => value + 1)), []);

  const trackKey = track?.key;
  useLayoutEffect(() => {
    if (trackKey) return;
    return () => {
      abortRef.current?.abort();
      const jobId = serverJobRef.current;
      serverJobRef.current = null;
      if (jobId) void deleteServerSummarize(jobId);
    };
  }, [trackKey]);

  async function handleSummarize() {
    if (track) {
      startSummarizeJob({
        key: track.key,
        scope: track.scope,
        ownerId: track.ownerId,
        fileName: track.fileName,
        kind,
        text,
        savedDocumentId: track.savedDocumentId,
      });
      return;
    }

    abortRef.current?.abort();
    const previousJobId = serverJobRef.current;
    if (previousJobId) void deleteServerSummarize(previousJobId);
    const controller = new AbortController();
    const serverJobId = crypto.randomUUID();
    abortRef.current = controller;
    serverJobRef.current = serverJobId;
    setLocalSummarizing(true);
    setLocalError(null);
    try {
      const result = await apiFetch<{ summary: string }>("/api/instructions/summarize", {
        method: "POST",
        body: JSON.stringify({ text, kind, jobId: serverJobId }),
        signal: controller.signal,
      });
      if (controller.signal.aborted || typeof result.summary !== "string" || !result.summary.trim()) return;
      onSummarizedRef.current(result.summary);
    } catch (reason: unknown) {
      if (controller.signal.aborted || isAbortError(reason)) return;
      setLocalError(reason instanceof Error ? reason.message : "Could not summarize instructions.");
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        serverJobRef.current = null;
        setLocalSummarizing(false);
      }
    }
  }

  function handleCancel() {
    if (track) {
      cancelSummarizeJob(track.key);
      return;
    }
    abortRef.current?.abort();
    const jobId = serverJobRef.current;
    serverJobRef.current = null;
    if (jobId) void deleteServerSummarize(jobId);
    setLocalSummarizing(false);
  }

  return (
    <div className="space-y-2">
      {status ? <UploadStatusText status={status} /> : null}
      <p
        className={cn(
          "text-xs",
          overLimit
            ? "text-[var(--tint-red-fg)] dark:text-red-400"
            : "text-[var(--lm-muted)] dark:text-slate-500"
        )}
      >
        {formatInstructionWordCount(words)}
      </p>
      {overLimit && !isSummarizing ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => void handleSummarize()}
        >
          Summarize
        </Button>
      ) : null}
      {isSummarizing ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" size="sm" disabled>
            Summarizing…
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      ) : null}
      {isSummarizing ? (
        <p className="text-xs text-[var(--lm-muted)] dark:text-slate-400">
          Summarizing in the background. You can keep working.
          {showStatus ? " Removing this PDF cancels the summary." : ""}
        </p>
      ) : null}
      {overLimit && note ? (
        <p className="text-xs text-[var(--lm-muted)] dark:text-slate-400">{note}</p>
      ) : null}
      {error ? (
        <p className="text-xs text-[var(--tint-red-fg)] dark:text-red-400">{error}</p>
      ) : null}
    </div>
  );
}

function isAbortError(reason: unknown): boolean {
  return reason instanceof Error && reason.name === "AbortError";
}
